import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  Button,
  DatePicker,
  Input,
  InputNumber,
  Result,
  Select,
  Space,
  Table,
  Tag,
} from "antd";
import type { FilterDropdownProps } from "antd/es/table/interface";
import { FilterOutlined, SearchOutlined, SnippetsOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs, { Dayjs } from "dayjs";
import { usePermissions } from "../../utils/usePermissions";
import { isSuperAdmin } from "../../utils/permissions";
import { BASE_API_URL } from "../../utils/consts";

const { RangePicker } = DatePicker;

interface TokenOption {
  _id: string;
  name: string;
  locationId?: { _id: string; name: string } | null;
}

interface PaymentType {
  Id: number;
  Name: string;
  Amount: number;
}

interface BillItem {
  Code: string;
  Name: string;
  Quantity: number;
  Price: number;
  TaxPercent: number;
  TotalPrice: number;
  DiscountAmount: number;
}

interface Bill {
  Id: number;
  FiscalReceiptCounter: string;
  FiscalReceiptNumber: string;
  TableName: string;
  UserFullName: string;
  TimeCreated: string;
  TimePrinted: string;
  ComputerName: string;
  BusinessPartnerTaxNumber: string;
  BusinessPartnerName: string;
  Amount: number;
  IsRevoked: boolean;
  PaymentTypes: PaymentType[];
  Items: BillItem[];
}

const fmt = (n: number) =>
  new Intl.NumberFormat("sr-RS", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n || 0) + " RSD";

const PAYMENT_COLOR: Record<string, string> = {
  gotovina: "green",
  kartica: "blue",
  "prenos na račun": "purple",
};

const normalize = (s: string) =>
  (s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();

const OctopusBills: React.FC = () => {
  const { t } = useTranslation();
  const userData = useSelector((state: any) => state.userData.user);
  const rawClientId = userData?.clientId;
  const clientId =
    typeof rawClientId === "object" ? rawClientId?._id : rawClientId;

  const { hasPermission } = usePermissions();
  const canView =
    isSuperAdmin(userData?.roles) || hasPermission("octopus-bills:view");

  const [tokens, setTokens] = useState<TokenOption[]>([]);
  const [selectedTokenId, setSelectedTokenId] = useState<string | undefined>();
  const [tokensLoading, setTokensLoading] = useState(false);

  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf("day"),
    dayjs().endOf("day"),
  ]);
  const [searchText, setSearchText] = useState("");

  const [bills, setBills] = useState<Bill[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  // Controlled table filter/sort state — lets us drive a "Reset filters" button
  // and keep every column filterable/sortable via one consistent mechanism.
  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({
    columnKey: "TimeCreated",
    order: "descend",
  });

  useEffect(() => {
    if (!clientId || !canView) return;
    setTokensLoading(true);
    axios
      .get(`${BASE_API_URL}/${clientId}/octopus-tokens`)
      .then((res) => {
        setTokens(res.data || []);
        if (res.data?.length === 1) setSelectedTokenId(res.data[0]._id);
      })
      .catch(() => {})
      .finally(() => setTokensLoading(false));
  }, [clientId]);

  const handleSearch = async () => {
    setLoading(true);
    setSearched(true);
    setFilteredInfo({});
    setSortedInfo({ columnKey: "TimeCreated", order: "descend" });
    try {
      const dateFrom = dateRange[0].format("YYYY-MM-DD");
      // Octopus treats "dateTo" as an exclusive boundary, so ask for the day
      // after the selected end date to include the whole last day.
      const dateTo = dateRange[1].add(1, "day").format("YYYY-MM-DD");
      const params: Record<string, string> = { dateFrom, dateTo };
      const token = tokens.find((tk) => tk._id === selectedTokenId);
      if (token?.locationId?._id) params.locationId = token.locationId._id;

      const res = await axios.get(`${BASE_API_URL}/${clientId}/octopus-tokens/bills`, {
        params,
      });
      setBills(res.data || []);
    } catch (err: any) {
      setBills([]);
    } finally {
      setLoading(false);
    }
  };

  const resetFilters = () => {
    setFilteredInfo({});
    setSortedInfo({});
    setSearchText("");
  };

  const searchedBills = useMemo(() => {
    if (!searchText.trim()) return bills;
    const q = normalize(searchText);
    return bills.filter(
      (b) =>
        normalize(b.FiscalReceiptNumber).includes(q) ||
        normalize(b.UserFullName).includes(q) ||
        normalize(b.BusinessPartnerName).includes(q) ||
        normalize(b.TableName).includes(q) ||
        (b.Items || []).some((item) => normalize(item.Name).includes(q))
    );
  }, [bills, searchText]);

  // Filter option lists are derived from the full search-range result (not the
  // currently filtered rows), so picking one filter never hides the others.
  const waiterFilters = useMemo(() => {
    const names = Array.from(new Set(bills.map((b) => b.UserFullName).filter(Boolean)));
    return names.sort((a, b) => a.localeCompare(b, "sr")).map((n) => ({ text: n, value: n }));
  }, [bills]);

  const tableFilters = useMemo(() => {
    const names = Array.from(new Set(bills.map((b) => b.TableName || "")));
    return names
      .sort((a, b) => a.localeCompare(b, "sr"))
      .map((n) => ({ text: n || "Bez stola", value: n }));
  }, [bills]);

  const paymentTypeFilters = useMemo(() => {
    const names = Array.from(
      new Set(bills.flatMap((b) => (b.PaymentTypes || []).map((pt) => pt.Name)))
    );
    return names.sort((a, b) => a.localeCompare(b, "sr")).map((n) => ({ text: n, value: n }));
  }, [bills]);

  const waiterOptions = useMemo(
    () => waiterFilters.map((f) => ({ label: f.text, value: f.value })),
    [waiterFilters]
  );
  const tableOptions = useMemo(
    () => tableFilters.map((f) => ({ label: f.text, value: f.value })),
    [tableFilters]
  );
  const paymentTypeOptions = useMemo(
    () => paymentTypeFilters.map((f) => ({ label: f.text, value: f.value })),
    [paymentTypeFilters]
  );

  const setColumnFilter = (key: string, value: any[]) => {
    setFilteredInfo((prev) => ({ ...prev, [key]: value.length ? value : null }));
  };

  // Applies every active column filter on top of the free-text search, so the
  // table rows and the summary totals below are always in sync with each other.
  const displayedBills = useMemo(() => {
    let result = searchedBills;
    const waiterVals: string[] | null = filteredInfo.UserFullName || null;
    if (waiterVals?.length) result = result.filter((b) => waiterVals.includes(b.UserFullName));
    const tableVals: string[] | null = filteredInfo.TableName || null;
    if (tableVals?.length) result = result.filter((b) => tableVals.includes(b.TableName || ""));
    const paymentVals: string[] | null = filteredInfo.PaymentTypes || null;
    if (paymentVals?.length)
      result = result.filter((b) =>
        (b.PaymentTypes || []).some((pt) => paymentVals.includes(pt.Name))
      );
    const statusVals: string[] | null = filteredInfo.IsRevoked || null;
    if (statusVals?.length)
      result = result.filter((b) => statusVals.includes(b.IsRevoked ? "revoked" : "active"));
    const amountVal: string[] | null = filteredInfo.Amount || null;
    if (amountVal?.length) {
      const [minStr, maxStr] = String(amountVal[0]).split(",");
      const min = minStr ? Number(minStr) : -Infinity;
      const max = maxStr ? Number(maxStr) : Infinity;
      result = result.filter((b) => b.Amount >= min && b.Amount <= max);
    }
    return result;
  }, [searchedBills, filteredInfo]);

  const activeBills = displayedBills.filter((b) => !b.IsRevoked);
  const totalAmount = activeBills.reduce((sum, b) => sum + (b.Amount || 0), 0);
  const revokedCount = displayedBills.filter((b) => b.IsRevoked).length;

  const paymentTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    activeBills.forEach((b) => {
      (b.PaymentTypes || []).forEach((pt) => {
        totals[pt.Name] = (totals[pt.Name] || 0) + (pt.Amount || 0);
      });
    });
    return totals;
  }, [activeBills]);

  const hasActiveFilters =
    Object.values(filteredInfo).some((v) => (Array.isArray(v) ? v.length > 0 : v != null)) ||
    !!searchText.trim();

  if (!canView) {
    return (
      <Result
        status="403"
        title="403"
        subTitle={t("You don't have permission to view this page.")}
      />
    );
  }

  const amountFilterDropdown = ({
    setSelectedKeys,
    selectedKeys,
    confirm,
    clearFilters,
  }: FilterDropdownProps) => {
    const [minStr, maxStr] = (selectedKeys[0] as string | undefined)?.split(",") || ["", ""];
    return (
      <div style={{ padding: 8 }}>
        <Space>
          <InputNumber
            placeholder="Min"
            value={minStr ? Number(minStr) : undefined}
            onChange={(v) => setSelectedKeys([`${v ?? ""},${maxStr ?? ""}`])}
            style={{ width: 100 }}
          />
          <InputNumber
            placeholder="Max"
            value={maxStr ? Number(maxStr) : undefined}
            onChange={(v) => setSelectedKeys([`${minStr ?? ""},${v ?? ""}`])}
            style={{ width: 100 }}
          />
        </Space>
        <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between" }}>
          <Button
            size="small"
            onClick={() => {
              clearFilters?.();
              setSelectedKeys([]);
              confirm();
            }}
          >
            Reset
          </Button>
          <Button size="small" type="primary" onClick={() => confirm()}>
            OK
          </Button>
        </div>
      </div>
    );
  };

  const columns = [
    {
      title: "Račun",
      dataIndex: "FiscalReceiptNumber",
      key: "FiscalReceiptNumber",
      sorter: (a: Bill, b: Bill) =>
        (a.FiscalReceiptNumber || a.FiscalReceiptCounter || "").localeCompare(
          b.FiscalReceiptNumber || b.FiscalReceiptCounter || "",
          "sr",
          { numeric: true }
        ),
      sortOrder: sortedInfo.columnKey === "FiscalReceiptNumber" ? sortedInfo.order : null,
      render: (v: string, row: Bill) => (
        <div>
          <code style={{ fontSize: 12 }}>{v || row.FiscalReceiptCounter}</code>
          {row.BusinessPartnerName && (
            <div style={{ fontSize: 11, color: "#9ca3af" }}>
              {row.BusinessPartnerName}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Vrijeme",
      dataIndex: "TimeCreated",
      key: "TimeCreated",
      sorter: (a: Bill, b: Bill) =>
        dayjs(a.TimeCreated).unix() - dayjs(b.TimeCreated).unix(),
      sortOrder: sortedInfo.columnKey === "TimeCreated" ? sortedInfo.order : null,
      render: (d: string) => (d ? dayjs(d).format("DD.MM.YYYY HH:mm") : "—"),
    },
    {
      title: "Konobar/Kasir",
      dataIndex: "UserFullName",
      key: "UserFullName",
      sorter: (a: Bill, b: Bill) =>
        (a.UserFullName || "").localeCompare(b.UserFullName || "", "sr"),
      sortOrder: sortedInfo.columnKey === "UserFullName" ? sortedInfo.order : null,
      filters: waiterFilters,
      filteredValue: filteredInfo.UserFullName || null,
      filterSearch: true,
      onFilter: (value: any, record: Bill) => record.UserFullName === value,
    },
    {
      title: "Sto",
      dataIndex: "TableName",
      key: "TableName",
      sorter: (a: Bill, b: Bill) =>
        (a.TableName || "").localeCompare(b.TableName || "", "sr"),
      sortOrder: sortedInfo.columnKey === "TableName" ? sortedInfo.order : null,
      filters: tableFilters,
      filteredValue: filteredInfo.TableName || null,
      filterSearch: true,
      onFilter: (value: any, record: Bill) => (record.TableName || "") === value,
      render: (v: string) => v || "—",
    },
    {
      title: "Način plaćanja",
      dataIndex: "PaymentTypes",
      key: "PaymentTypes",
      sorter: (a: Bill, b: Bill) =>
        (a.PaymentTypes?.[0]?.Name || "").localeCompare(b.PaymentTypes?.[0]?.Name || "", "sr"),
      sortOrder: sortedInfo.columnKey === "PaymentTypes" ? sortedInfo.order : null,
      filters: paymentTypeFilters,
      filteredValue: filteredInfo.PaymentTypes || null,
      onFilter: (value: any, record: Bill) =>
        (record.PaymentTypes || []).some((pt) => pt.Name === value),
      render: (types: PaymentType[]) => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {(types || []).map((pt) => (
            <Tag key={pt.Id} color={PAYMENT_COLOR[pt.Name?.toLowerCase()] || "default"}>
              {pt.Name}
            </Tag>
          ))}
        </div>
      ),
    },
    {
      title: "Iznos",
      dataIndex: "Amount",
      key: "Amount",
      align: "right" as const,
      sorter: (a: Bill, b: Bill) => a.Amount - b.Amount,
      sortOrder: sortedInfo.columnKey === "Amount" ? sortedInfo.order : null,
      filteredValue: filteredInfo.Amount || null,
      filterIcon: (filtered: boolean) => (
        <FilterOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
      ),
      filterDropdown: amountFilterDropdown,
      onFilter: (value: any, record: Bill) => {
        const [minStr, maxStr] = String(value).split(",");
        const min = minStr ? Number(minStr) : -Infinity;
        const max = maxStr ? Number(maxStr) : Infinity;
        return record.Amount >= min && record.Amount <= max;
      },
      render: (v: number, row: Bill) => (
        <strong style={row.IsRevoked ? { textDecoration: "line-through", color: "#9ca3af" } : {}}>
          {fmt(v)}
        </strong>
      ),
    },
    {
      title: "Status",
      dataIndex: "IsRevoked",
      key: "IsRevoked",
      sorter: (a: Bill, b: Bill) => Number(a.IsRevoked) - Number(b.IsRevoked),
      sortOrder: sortedInfo.columnKey === "IsRevoked" ? sortedInfo.order : null,
      filters: [
        { text: "Aktivan", value: "active" },
        { text: "Stornirano", value: "revoked" },
      ],
      filteredValue: filteredInfo.IsRevoked || null,
      onFilter: (value: any, record: Bill) =>
        value === "revoked" ? record.IsRevoked : !record.IsRevoked,
      render: (revoked: boolean) =>
        revoked ? (
          <Tag icon={<SnippetsOutlined />} color="red">
            Stornirano
          </Tag>
        ) : (
          <Tag color="green">Aktivan</Tag>
        ),
    },
  ];

  return (
    <div style={{ padding: "24px 24px 0" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 24,
          flexWrap: "wrap",
        }}
      >
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#111827" }}>
          Računi
        </h1>
      </div>

      {/* Filters */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 20,
          flexWrap: "wrap",
          background: "#f9fafb",
          border: "1px solid #e5e7eb",
          borderRadius: 10,
          padding: "12px 16px",
        }}
      >
        <RangePicker
          value={dateRange}
          onChange={(vals) => {
            if (vals?.[0] && vals?.[1]) setDateRange([vals[0], vals[1]]);
          }}
          format="DD.MM.YYYY"
          allowClear={false}
          style={{ minWidth: 240 }}
        />

        <Select
          placeholder="Izaberite lokaciju"
          value={selectedTokenId}
          onChange={setSelectedTokenId}
          loading={tokensLoading}
          style={{ minWidth: 220 }}
          optionLabelProp="label"
          options={tokens.map((tk) => ({
            value: tk._id,
            label: tk.name,
            desc: tk.locationId?.name,
          }))}
          optionRender={(opt) => (
            <div>
              <div style={{ fontWeight: 600 }}>{opt.data.label}</div>
              {opt.data.desc && (
                <div style={{ fontSize: 11, color: "#9ca3af" }}>{opt.data.desc}</div>
              )}
            </div>
          )}
          notFoundContent={
            tokensLoading
              ? "Učitavanje..."
              : "Nema podešenih lokacija. Dodajte ih u Podešavanja → Octopus."
          }
        />

        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={handleSearch}
          loading={loading}
          disabled={!selectedTokenId}
        >
          Pretraži
        </Button>

        {bills.length > 0 && (
          <Input
            placeholder="Pretraži po broju, kasiru, stolu, partneru, artiklu..."
            prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            style={{ minWidth: 280, flex: 1 }}
          />
        )}

        {hasActiveFilters && (
          <Button onClick={resetFilters}>Resetuj filtere</Button>
        )}
      </div>

      {/* Advanced filters — extracted directly from the loaded bills, so every
          waiter/table/payment type that actually appears in the results can be
          picked without needing to know it in advance. */}
      {bills.length > 0 && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 20,
            flexWrap: "wrap",
          }}
        >
          <Select
            mode="multiple"
            allowClear
            placeholder="Filtriraj po konobaru/kasiru"
            value={filteredInfo.UserFullName || []}
            onChange={(vals) => setColumnFilter("UserFullName", vals)}
            options={waiterOptions}
            maxTagCount="responsive"
            style={{ minWidth: 240 }}
          />
          <Select
            mode="multiple"
            allowClear
            placeholder="Filtriraj po stolu"
            value={filteredInfo.TableName || []}
            onChange={(vals) => setColumnFilter("TableName", vals)}
            options={tableOptions}
            maxTagCount="responsive"
            style={{ minWidth: 200 }}
          />
          <Select
            mode="multiple"
            allowClear
            placeholder="Filtriraj po načinu plaćanja"
            value={filteredInfo.PaymentTypes || []}
            onChange={(vals) => setColumnFilter("PaymentTypes", vals)}
            options={paymentTypeOptions}
            maxTagCount="responsive"
            style={{ minWidth: 220 }}
          />
          <Select
            placeholder="Status"
            allowClear
            value={filteredInfo.IsRevoked?.[0]}
            onChange={(val) => setColumnFilter("IsRevoked", val ? [val] : [])}
            options={[
              { value: "active", label: "Samo aktivni" },
              { value: "revoked", label: "Samo stornirani" },
            ]}
            style={{ minWidth: 160 }}
          />
          <InputNumber
            placeholder="Min iznos"
            value={
              filteredInfo.Amount?.[0] ? Number(String(filteredInfo.Amount[0]).split(",")[0]) || undefined : undefined
            }
            onChange={(v) => {
              const max = filteredInfo.Amount?.[0]?.split(",")[1] || "";
              setColumnFilter("Amount", v == null && !max ? [] : [`${v ?? ""},${max}`]);
            }}
            style={{ width: 110 }}
          />
          <InputNumber
            placeholder="Max iznos"
            value={
              filteredInfo.Amount?.[0] ? Number(String(filteredInfo.Amount[0]).split(",")[1]) || undefined : undefined
            }
            onChange={(v) => {
              const min = filteredInfo.Amount?.[0]?.split(",")[0] || "";
              setColumnFilter("Amount", v == null && !min ? [] : [`${min},${v ?? ""}`]);
            }}
            style={{ width: 110 }}
          />
        </div>
      )}

      {/* Summary row */}
      {searched && !loading && displayedBills.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: 24,
            marginBottom: 16,
            flexWrap: "wrap",
            fontSize: 13,
            color: "#6b7280",
          }}
        >
          <span>
            Ukupno računa:{" "}
            <strong style={{ color: "#111827" }}>{displayedBills.length}</strong>
          </span>
          <span>
            Ukupan iznos:{" "}
            <strong style={{ color: "#111827" }}>{fmt(totalAmount)}</strong>
          </span>
          {Object.entries(paymentTotals).map(([name, amount]) => (
            <span key={name}>
              {name}:{" "}
              <strong style={{ color: "#111827" }}>{fmt(amount)}</strong>
            </span>
          ))}
          {revokedCount > 0 && (
            <span>
              Stornirano: <strong style={{ color: "#ef4444" }}>{revokedCount}</strong>
            </span>
          )}
        </div>
      )}

      <Table
        dataSource={displayedBills}
        columns={columns}
        rowKey="Id"
        loading={loading}
        pagination={{ pageSize: 20, showSizeChanger: false }}
        rowClassName={(row: Bill) => (row.IsRevoked ? "octopus-bill-revoked-row" : "")}
        onChange={(_pagination, filters, sorter) => {
          setFilteredInfo(filters);
          setSortedInfo(Array.isArray(sorter) ? sorter[0] || {} : sorter);
        }}
        expandable={{
          rowExpandable: (row) => (row.Items || []).length > 0,
          expandedRowRender: (row) => (
            <div style={{ padding: "8px 16px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ color: "#6b7280", textAlign: "left" }}>
                    <th style={{ padding: "4px 8px" }}>Artikal</th>
                    <th style={{ padding: "4px 8px", textAlign: "right" }}>Kol.</th>
                    <th style={{ padding: "4px 8px", textAlign: "right" }}>Cena</th>
                    <th style={{ padding: "4px 8px", textAlign: "right" }}>PDV %</th>
                    <th style={{ padding: "4px 8px", textAlign: "right" }}>Ukupno</th>
                  </tr>
                </thead>
                <tbody>
                  {(row.Items || []).map((item, idx) => (
                    <tr key={idx} style={{ borderTop: "1px solid #e5e7eb" }}>
                      <td style={{ padding: "4px 8px" }}>
                        {item.Name}
                        {item.Code && (
                          <span style={{ color: "#9ca3af", marginLeft: 6 }}>
                            ({item.Code})
                          </span>
                        )}
                      </td>
                      <td style={{ padding: "4px 8px", textAlign: "right" }}>
                        {item.Quantity}
                      </td>
                      <td style={{ padding: "4px 8px", textAlign: "right" }}>
                        {fmt(item.Price)}
                      </td>
                      <td style={{ padding: "4px 8px", textAlign: "right" }}>
                        {item.TaxPercent}%
                      </td>
                      <td style={{ padding: "4px 8px", textAlign: "right", fontWeight: 600 }}>
                        {fmt(item.TotalPrice)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ),
        }}
        locale={{
          emptyText: searched
            ? "Nema računa za izabrani period i filtere"
            : "Izaberite period i lokaciju, pa kliknite Pretraži",
        }}
        scroll={{ x: 1000 }}
      />

      <style>{`
        .octopus-bill-revoked-row td {
          background: #fef2f2 !important;
        }
        .octopus-bill-revoked-row:hover td {
          background: #fee2e2 !important;
        }
      `}</style>
    </div>
  );
};

export default OctopusBills;
